/**
 * The selected RULE: which sibling it is, and the condition that takes it.
 *
 * ## Why a rule and not a "connection"
 *
 * It used to be titled 连线 and described the line: where it runs from and to, which
 * detour lane it uses, how to read its expression. All of that is visible on the canvas
 * already — the line is drawn between the two cards, with its condition on it. What is
 * NOT visible, and what this panel exists for, is the condition as something you can
 * change. So the panel is named after the record it edits: one entry in the source
 * question's `nextQuestionRules`.
 *
 * ## Why the siblings are here
 *
 * `nextQuestionRules` is first-match-wins, so a rule is not meaningful on its own — it
 * means "this, given none of the ones above it matched". Its siblings are therefore part
 * of reading it, and switching between them without going back to the canvas is how you
 * compare them. The 第 X / N 条 badge is also the navigation, so the position is visible
 * wherever you are in the set.
 *
 * ## What this panel deliberately does NOT do
 *
 * Reordering and deleting were both here and were removed on purpose, to leave the panel
 * to the condition. Two consequences worth knowing:
 *
 *   - Deleting a rule is still reachable — select the line and press Delete, which goes
 *     through `TreeCanvas`'s `onEdgesDelete`.
 *   - Evaluation ORDER now has no editor anywhere. That matters, because position
 *     decides whether a rule can fire at all: `add-branch` appends a `true` catch-all,
 *     and anything added after it is dead on arrival. The `isDeadBranch` alert still
 *     reports when that has happened, but fixing it means editing conditions rather than
 *     moving rules. The `move-rule` reducer action is unchanged and still tested, so
 *     bringing the control back is UI-only.
 *
 * ## Why the condition editor refuses some conditions
 *
 * The editor draws ONE level of boolean logic (see `condition.ts`). Live conditions
 * include arithmetic over fallback chains, `hasKey` and `hasAnyMatch`, none of which is
 * one level — and re-serialising one through a model that cannot hold it would silently
 * change the rule. Those fall back to the expression itself, read-only in the structured
 * view and editable as text, rather than being approximated.
 */
import {
  Alert,
  Badge,
  Box,
  Button,
  Container,
  FormField,
  Header,
  Input,
  Select,
  SegmentedControl,
  SpaceBetween,
  Textarea,
} from '@amzn/awsui-components-react';

import { useEffect, useMemo, useState } from 'react';

import type { QuestionnaireDefinition } from '../../types';
import {
  type FlatCondition,
  OPS_FOR_KIND,
  OP_LABEL,
  type Term,
  flatIsComplete,
  freshTerm,
  fromFlat,
  toFlat,
  upstreamVariables,
} from '../../utils/condition';
import type { CpaEdge } from '../../utils/projection';
import { graphFactsOf, isDerivedEdge } from '../../utils/projection';
import './EdgeInspector.css';

export interface EdgeInspectorProps {
  definition: QuestionnaireDefinition;
  edge: CpaEdge | null;
  readOnly: boolean;
  /** `null` turns the edge unconditional; refused when siblings exist. */
  onSetCondition: (sourceNodeId: string, targetNodeId: string, expression: string | null) => void;
  /**
   * Select a sibling rule of the same source, by 0-based index.
   *
   * The page resolves it to an edge id: this component knows the rule list from the
   * definition but not how the projection named the edges, and duplicating that id
   * format here is how the two would drift.
   */
  onSelectSibling?: (sourceNodeId: string, ruleIndex: number) => void;
  /** Dismiss the panel. Present in both modes — see NodeInspector's `onClose`. */
  onClose: () => void;
}

export default function EdgeInspector({
  definition,
  edge,
  readOnly,
  onSetCondition,
  onSelectSibling,
  onClose,
}: EdgeInspectorProps) {
  // Draft of the expression being typed, so an in-progress edit is not pushed into the
  // definition on every keystroke (each one would land on the undo stack).
  const [exprDraft, setExprDraft] = useState('');
  useEffect(() => setExprDraft(edge?.label ?? ''), [edge?.id, edge?.label]);

  /*
   * Draft of the STRUCTURED condition, for the same reason: picking a variable before
   * picking its value would otherwise write a half-built term into the definition, and
   * a half-built term is a rule that matches nothing.
   */
  const [draft, setDraft] = useState<FlatCondition | null>(null);
  const facts = useMemo(() => graphFactsOf(definition), [definition]);
  const parsed = useMemo(
    () => (edge?.label ? toFlat(edge.label, definition, facts) : null),
    [edge?.label, definition, facts],
  );
  useEffect(() => setDraft(parsed), [parsed]);

  const variables = useMemo(
    () => (edge ? upstreamVariables(definition, facts, edge.source) : []),
    [edge, definition, facts],
  );

  if (!edge) return null;

  // Eligibility lines never reach here: clicking one selects its rule label instead
  // (TreeCanvas.handleEdgeClick), whose node inspector says everything the line could.
  if (isDerivedEdge(edge.kind)) return null;

  const isConditional = edge.kind === 'when';
  const idx0 = (edge.ruleIndex ?? 1) - 1;
  const count = edge.ruleCount ?? 1;
  const varOf = (ref: string) => variables.find((v) => v.ref === ref);

  /** Write the structured draft through, serialised by the AST. */
  const applyDraft = (next: FlatCondition) => {
    setDraft(next);
    if (flatIsComplete(next)) onSetCondition(edge.source, edge.target, fromFlat(next));
  };
  const patchTerm = (i: number, patch: Partial<Term>) => {
    if (!draft) return;
    applyDraft({
      ...draft,
      terms: draft.terms.map((t, j) => (j === i ? { ...t, ...patch } : t)),
    });
  };

  return (
    <Container
      header={
        <Header
          variant="h3"
          description={isConditional ? '条件规则' : '无条件规则'}
          actions={
            /*
             * Sibling navigation lives ON the 第 X / N 条 badge rather than in a row of
             * its own. The badge already says which of the siblings this is, so the
             * arrows belong next to the number they move — and a first-match-wins rule
             * is read by stepping through its siblings, which is a frequent enough move
             * to not deserve a labelled section.
             *
             * Deliberately NOT the same glyphs as 上移/下移 below: these change WHICH
             * rule you are looking at, those change the evaluation order. Chevrons for
             * navigation, carets for reordering, and both carry a title.
             */
            <SpaceBetween direction="horizontal" size="xxs">
              {isConditional && count > 1 && onSelectSibling && (
                <>
                  <Button
                    variant="inline-icon"
                    iconName="angle-left"
                    ariaLabel="上一条兄弟规则"
                    disabled={idx0 === 0}
                    onClick={() => onSelectSibling(edge.source, idx0 - 1)}
                  />
                  <Badge color={edge.isDeadBranch ? 'red' : 'grey'}>
                    第 {edge.ruleIndex} / {count} 条
                  </Badge>
                  <Button
                    variant="inline-icon"
                    iconName="angle-right"
                    ariaLabel="下一条兄弟规则"
                    disabled={idx0 >= count - 1}
                    onClick={() => onSelectSibling(edge.source, idx0 + 1)}
                  />
                </>
              )}
              {isConditional && count > 1 && !onSelectSibling && (
                <Badge color={edge.isDeadBranch ? 'red' : 'grey'}>
                  第 {edge.ruleIndex} / {count} 条
                </Badge>
              )}
              <Button variant="icon" iconName="close" ariaLabel="关闭" onClick={onClose} />
            </SpaceBetween>
          }
        >
          规则
        </Header>
      }
    >
      <SpaceBetween size="m">
        {edge.isDeadBranch && (
          <Alert type="error" header="这条规则永远不会被取用">
            规则是<strong>第一个为 true 的胜出</strong>。它排在默认规则之后，或与前面某条
            条件完全相同，所以轮不到它 —— 改掉它的条件，或者改掉挡在它前面那条。
          </Alert>
        )}

        {/*
          * Which editor, in priority order:
          *
          *   draft           the structured one — either parsed from the stored
          *                   expression, or seeded empty by 「改为条件规则」 below
          *   isConditional   a stored condition the flat model cannot hold; raw text
          *   else            an unconditional edge, offering the conversion
          *
          * `draft` is tested FIRST and outside the conditional/unconditional split, so
          * seeding it on a static edge shows the same rows the conditional case gets.
          * Nesting it inside the split is what made 「改为条件规则」 appear to do nothing.
          */}
        {draft ? (
          <>
            {
              /*
               * The structured editor: one row per comparison, one connective for the
               * whole list. Nesting is deliberately absent — see `condition.ts`.
               */
              <FormField
                label="条件"
                description="引用上游已经作答的问题。只支持一级逻辑：整组条件共用一个 AND 或 OR。"
              >
                <SpaceBetween size="xs">
                  {draft.terms.length > 1 && (
                    <SegmentedControl
                      selectedId={draft.join}
                      onChange={({ detail }) =>
                        applyDraft({ ...draft, join: detail.selectedId as 'and' | 'or' })
                      }
                      label="连接方式"
                      options={[
                        { id: 'and', text: '全部满足 (AND)' },
                        { id: 'or', text: '任一满足 (OR)' },
                      ]}
                    />
                  )}

                  {draft.terms.map((t, i) => {
                    const v = varOf(t.ref);
                    // A term whose variable is not upstream of here is the
                    // `ref-not-dominating` defect. Shown rather than dropped: it is in
                    // the data, and hiding it would make the rule look different from
                    // what the engine runs.
                    const ops = v ? OPS_FOR_KIND[v.kind] : ([t.op] as const);
                    return (
                      <div key={i} className="cpa-term">
                        <div className="cpa-term__var">
                          <Select
                            expandToViewport
                            disabled={readOnly}
                            selectedOption={
                              v
                                ? { value: v.ref, label: v.label, description: v.nodeId }
                                : { value: t.ref, label: t.ref }
                            }
                            options={variables.map((x) => ({
                              value: x.ref,
                              label: x.label,
                              description: x.nodeId,
                            }))}
                            onChange={({ detail }) => {
                              const next = varOf(detail.selectedOption.value!);
                              // Changing the variable changes which operators and
                              // values are legal, so the term is rebuilt rather than
                              // patched — keeping the old operator could leave
                              // `contains` on a number.
                              if (next) patchTerm(i, freshTerm(next));
                            }}
                          />
                        </div>
                        <div className="cpa-term__op">
                          <Select
                            expandToViewport
                            disabled={readOnly || ops.length === 1}
                            selectedOption={{ value: t.op, label: OP_LABEL[t.op] }}
                            options={ops.map((o) => ({ value: o, label: OP_LABEL[o] }))}
                            onChange={({ detail }) =>
                              patchTerm(i, { op: detail.selectedOption.value as Term['op'] })
                            }
                          />
                        </div>
                        <div className="cpa-term__val">
                          {v && v.kind === 'number' ? (
                            <Input
                              type="number"
                              disabled={readOnly}
                              value={String(t.value)}
                              onChange={({ detail }) =>
                                patchTerm(i, { value: Number(detail.value) })
                              }
                            />
                          ) : (
                            <Select
                              expandToViewport
                              disabled={readOnly}
                              placeholder="选项"
                              selectedOption={
                                t.value === ''
                                  ? null
                                  : {
                                      value: String(t.value),
                                      label:
                                        v?.options.find((o) => o.value === t.value)?.label ??
                                        String(t.value),
                                    }
                              }
                              options={(v?.options ?? []).map((o) => ({
                                value: o.value,
                                label: o.label,
                                description: `value = ${o.value}`,
                              }))}
                              onChange={({ detail }) =>
                                patchTerm(i, { value: detail.selectedOption.value! })
                              }
                            />
                          )}
                        </div>
                        <Button
                          variant="inline-icon"
                          iconName="remove"
                          ariaLabel="删除这一条"
                          // The last term cannot go: a rule with no condition is a
                          // different thing (an unconditional edge), reached by its own
                          // button so the consequence can be stated.
                          disabled={readOnly || draft.terms.length <= 1}
                          onClick={() =>
                            applyDraft({ ...draft, terms: draft.terms.filter((_, j) => j !== i) })
                          }
                        />
                      </div>
                    );
                  })}

                  <Button
                    iconName="add-plus"
                    disabled={readOnly || variables.length === 0}
                    onClick={() =>
                      applyDraft({ ...draft, terms: [...draft.terms, freshTerm(variables[0])] })
                    }
                  >
                    添加条件
                  </Button>

                  {/* The serialised result, so what gets stored is never a surprise. */}
                  {flatIsComplete(draft) && (
                    <Box variant="code" fontSize="body-s">
                      {fromFlat(draft)}
                    </Box>
                  )}
                </SpaceBetween>
              </FormField>
            }
          </>
        ) : isConditional ? (
          /*
           * A stored condition the flat model cannot hold. Shown as text instead of
           * being folded into terms, because folding it would change it.
           */
          <FormField
            label="条件"
            description={
              edge.isDefaultBranch
                ? '这是默认规则：条件恒为 true，即「其它情况都走这条」。'
                : '这条条件不是一级布尔逻辑（可能含算术、coalesce、hasKey 或 hasAnyMatch），结构化编辑器无法在不改变它的前提下显示它 —— 直接编辑原文。'
            }
          >
            <SpaceBetween size="xs">
              <Textarea
                value={exprDraft}
                disabled={readOnly}
                rows={3}
                placeholder="metric['Q_1_1_5'] === 'a'"
                onChange={({ detail }) => setExprDraft(detail.value)}
              />
              <Button
                disabled={readOnly || !exprDraft.trim() || exprDraft === edge.label}
                onClick={() => onSetCondition(edge.source, edge.target, exprDraft)}
              >
                应用条件
              </Button>
            </SpaceBetween>
          </FormField>
        ) : (
          /*
           * An unconditional edge, i.e. `nextNodeId`.
           *
           * It gets the SAME structured editor, seeded empty, rather than only a text
           * box. Converting a static edge to a conditional one is an ordinary edit — it
           * is what you do as soon as a question grows a second outcome — and sending
           * that one path through raw expression text while every other condition edit
           * is a picker is the kind of split that makes the raw box feel like the real
           * interface.
           *
           * `setEdgeCondition` does the conversion: the static edge becomes the single
           * rule and `nextNodeId` is removed. No `true` catch-all is invented, so until
           * a second rule exists the question has exactly one way out, guarded.
           */
          <FormField
            label="条件"
            description="这条目前是无条件规则：引擎看到它就不再求值这个问题上的任何条件规则。加上条件后它才会被判。"
          >
            <SpaceBetween size="xs">
              <Button
                iconName="add-plus"
                disabled={readOnly || variables.length === 0}
                onClick={() => setDraft({ join: 'and', terms: [freshTerm(variables[0])] })}
              >
                改为条件规则
              </Button>
              {variables.length === 0 && (
                <Box variant="small" color="text-status-inactive">
                  上游还没有可引用的问题 —— 条件只能引用到这里为止必然已作答的问题。
                </Box>
              )}
              <Textarea
                value={exprDraft}
                disabled={readOnly}
                rows={2}
                placeholder="metric['Q_1_1_5'] === 'a'"
                onChange={({ detail }) => setExprDraft(detail.value)}
              />
              <Button
                disabled={readOnly || !exprDraft.trim()}
                onClick={() => onSetCondition(edge.source, edge.target, exprDraft)}
              >
                应用条件
              </Button>
            </SpaceBetween>
          </FormField>
        )}

        {/*
          * The reverse conversion, outside the three-way split so it reads the same
          * whether the condition came up structured or as raw text — it is a property of
          * the EDGE, not of how its condition happens to be displayed.
          *
          * Legal only as the question's sole out-edge, and that is not a UI nicety:
          * `nextNodeId` makes the engine install a static calculator and stop evaluating
          * `nextQuestionRules` entirely, so doing this with siblings present would
          * silently disable every one of them. `setEdgeCondition` refuses it too — the
          * disabled state exists so the reason is readable before the click rather than
          * the press appearing to do nothing.
          */}
        {isConditional && (
          <SpaceBetween size="xxs">
            <Button
              disabled={readOnly || count > 1}
              onClick={() => onSetCondition(edge.source, edge.target, null)}
            >
              改为无条件规则
            </Button>
            {count > 1 && (
              <Box variant="small" color="text-status-inactive">
                这道题有 {count} 条出边，所以不能改成无条件 ——
                引擎看到无条件边就不再求值任何条件规则，会把其它分支全部静默关掉。
              </Box>
            )}
          </SpaceBetween>
        )}
      </SpaceBetween>
    </Container>
  );
}
